<script setup>
import {
  DIRECTION_DELIVERY,
  CARRIER_BOOKED_ORDER,
  LOADED_AT_SENDER,
  ARRIVED_BY_ROAD,
  COLLECTING_TRUCK_ARRIVED,
  CARRIER_LOADED_ON_RAIL,
  CARRIER_BOOKED_PICKUP,
  DELIVERED_TO_TERMINAL,
  NOT_DEPARTED_BY_RAIL,
  CARRIER_BOOKED_ARRIVAL,
  CARRIER_DEPARTED_BY_RAIL,
  CARRIER_ARRIVED_BY_RAIL,
  PICKED_UP_AT_TERMINAL,
  DELIVERED_TO_CONSIGNEE,
  DRIVER,
  TOUR,
  DONE,
  DIRECTION_PICKUP,
} from "@/utils/constants";
import { computed, ref } from "vue";
import { i18n } from "@/i18n/i18n";
import store from "@/store";
import Order from "@/data/interfaces/OrderInterface";
import ParamsData from "@/data/interfaces/ParamsDataInterface";
import {
  geofencesMatchTerminalId,
  geofencesResume,
  error,
} from "@/services/geofence.service";
import AppModal from "./AppModal.vue";
import MapIcon from "@/components/icons/MapIcon.vue";
import GpsIcon from "./icons/GpsIcon.vue";

const emit = defineEmits(["close"]);

const isNotInTerminal = ref(false);
const hasNoSignal = ref(false);

const props = defineProps({
  order: {
    type: Order,
    default: () => ({
      orderId: null,
      orderPosition: null,
      primaryReference: null,
      secondaryReference: null,
      directionType: null,
      transportationStatus: null,
      loading_unit_formatted: null,
      loadUnit: {
        identifierPrefix: null,
        identifierNumber: null,
        type: null,
        lengthCode: null,
        heightCode: null,
        emptyWeight: null,
        loadWeight: null,
        totalWeight: null,
        loadingStatus: null,
        dangerousGoods: false, // Set default value for boolean properties
        waste: false,
        limitedQuantity: false,
        customsGoods: false,
      },
      train: {
        trainNumber: null,
        operatorId: null,
        operatorName: null,
        shippingDate: null,
        trainClosingTime: null,
        trainArrivalTime: null,
        trainState: null,
      },
      terminal: {
        terminalId: {
          locationCode: null,
          authority: null,
        },
        terminalName: null,
      },
      freightForwarderId: null,
      freightForwarderName: null,
      licensePlate: null,
      driverId: null,
    }),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  index: Number,
  variant: {
    type: String,
    default: "brand-primary",
  },
  classList: {
    type: String,
    default: "w-full hydrated",
  },
  editedOrder: {
    type: Order,
    default: null,
    required: false,
  },
  params: {
    type: ParamsData,
    required: true,
  },
});

const notReadyForPickupStatus = [
  CARRIER_BOOKED_ORDER,
  LOADED_AT_SENDER,
  ARRIVED_BY_ROAD,
  CARRIER_BOOKED_ARRIVAL,
  DELIVERED_TO_TERMINAL,
  CARRIER_LOADED_ON_RAIL,
  CARRIER_DEPARTED_BY_RAIL,
];

const setButtonString = computed(() => {
  if (props.order.roadTransportAssignmentStatus == DRIVER) {
    return i18n.global.t("order.add_tour");
  } else if ([TOUR, DONE].includes(props.order.roadTransportAssignmentStatus)) {
    if (props.order.directionType === DIRECTION_DELIVERY) {
      if (props.order.transportationStatus === CARRIER_BOOKED_ORDER) {
        return i18n.global.t("tour.taken_over_at_sender");
      }
      if (props.order.transportationStatus === LOADED_AT_SENDER) {
        return i18n.global.t("tour.arrival_terminal");
      } else if (
        [
          ARRIVED_BY_ROAD,
          CARRIER_BOOKED_ARRIVAL,
          CARRIER_LOADED_ON_RAIL,
          CARRIER_DEPARTED_BY_RAIL,
          DELIVERED_TO_TERMINAL,
        ].includes(props.order.transportationStatus)
      ) {
        return i18n.global.t("tour.delivered_to_terminal");
      }
    } else {
      // DIRECTION_PICKUP
      if (notReadyForPickupStatus.includes(props.order.transportationStatus)) {
        return i18n.global.t("tour.not_ready_for_pickup");
      } else if (
        [
          CARRIER_BOOKED_ARRIVAL,
          CARRIER_DEPARTED_BY_RAIL,
          CARRIER_ARRIVED_BY_RAIL,
        ].includes(props.order.transportationStatus)
      ) {
        return i18n.global.t("tour.arrival_terminal");
      } else if (props.order.transportationStatus === CARRIER_BOOKED_PICKUP) {
        return i18n.global.t("tour.loaded_at_terminal");
      } else if (
        [PICKED_UP_AT_TERMINAL, DELIVERED_TO_CONSIGNEE].includes(
          props.order.transportationStatus
        )
      ) {
        return i18n.global.t("tour.delivered_to_consignee");
      }
    }
  }
  return "placeholder"; // TODO placeholder
});

const buttonDisabled = computed(() => {
  if ([TOUR, DONE].includes(props.order.roadTransportAssignmentStatus)) {
    if (props.order.directionType === DIRECTION_PICKUP) {
      if (notReadyForPickupStatus.includes(props.order.transportationStatus)) {
        return true;
      }
    }
  }
  return props.disabled;
});

const submit = async () => {
  await store.dispatch("toggleLoading", true);
  let params = { ...props.params };
  let newTransportationStatus = "";

  if (props.order.roadTransportAssignmentStatus == DRIVER) {
    await saveAndDispatch("add", params);
    return;
  }
  if (props.order.directionType === DIRECTION_DELIVERY) {
    switch (props.order.transportationStatus) {
      case CARRIER_BOOKED_ORDER:
        newTransportationStatus = LOADED_AT_SENDER;
        break;
      case LOADED_AT_SENDER:
        newTransportationStatus = ARRIVED_BY_ROAD;
        break;
      case CARRIER_BOOKED_ARRIVAL:
        newTransportationStatus = DELIVERED_TO_TERMINAL;
        break;
      case NOT_DEPARTED_BY_RAIL: // TODO not sure what this is
        newTransportationStatus = ARRIVED_BY_ROAD;
        break;
      default:
        break;
    }
  } else {
    switch (props.order.transportationStatus) {
      case CARRIER_DEPARTED_BY_RAIL:
      case CARRIER_ARRIVED_BY_RAIL:
      case NOT_DEPARTED_BY_RAIL:
      case LOADED_AT_SENDER:
      case CARRIER_BOOKED_ORDER:
        newTransportationStatus = COLLECTING_TRUCK_ARRIVED;
        break;
      case CARRIER_BOOKED_PICKUP:
        newTransportationStatus = PICKED_UP_AT_TERMINAL;
        break;
      case PICKED_UP_AT_TERMINAL:
        newTransportationStatus = DELIVERED_TO_CONSIGNEE;
        break;
      default:
        break;
    }
  }
  var geoFenceLocation = true;
  if (
    newTransportationStatus == COLLECTING_TRUCK_ARRIVED ||
    newTransportationStatus == ARRIVED_BY_ROAD
  ) {
    geoFenceLocation = await geofencesMatchTerminalId(
      props.order.terminal.terminalId.locationCode,
      props.order.terminal.terminalId.authority
    ).catch((err) => {
      store.dispatch("toggleLoading", false);
    });

    if (error?.value?.message == "User denied Geolocation") {
      hasNoSignal.value = true;
      isNotInTerminal.value = false;
      store.dispatch("toggleLoading", false);
      return geofencesResume();
    } else {
      if (!geoFenceLocation) {
        isNotInTerminal.value = true;
        hasNoSignal.value = false;
        store.dispatch("toggleLoading", false);
      }
    }
  }

  if (newTransportationStatus && geoFenceLocation) {
    params = {
      transportationStatus: newTransportationStatus,
    };
    if (
      newTransportationStatus === DELIVERED_TO_CONSIGNEE ||
      newTransportationStatus === DELIVERED_TO_TERMINAL
    ) {
      params = { ...params, roadTransportAssignmentStatus: "DONE" };
    }
    await saveAndDispatch("update", params);
    return;
  } else {
    console.error(
      "We don't know how to handle this transportationStatus yet. Please update code."
    );
    await store.dispatch("toggleLoading", false);
  }

  emit("close");
  await store.dispatch("toggleLoading", false);
};

const saveAndDispatch = async (
  dispatchType = "add" | "update",
  params = {}
) => {
  params = { ...params, driverId: store.state.user.data.user_id };
  let data = { order: props.order, index: props.index, params };
  try {
    await store
      .dispatch(`OrderStore/${dispatchType}TourOrder`, data)
      .catch((error) => {
        if (error.message.includes("parallel")) {
          store.dispatch("ErrorModalStore/toggleErrorModal", {
            errorType: "parallelEditError",
          });
        } else if (error.message.includes("failed")) {
          store.dispatch("ErrorModalStore/toggleErrorModal", {
            errorType: "updateOrderError",
          });
        }
      });
    emit("close");
    await store.dispatch("toggleLoading", false);
  } catch (error) {
    store.dispatch("toggleLoading", false);
    if (error.message.includes("parallel")) {
      store.dispatch("ErrorModalStore/toggleErrorModal", {
        errorType: "parallelEditError",
      });
    } else if (error.message.includes("Failed")) {
      store.dispatch("ErrorModalStore/toggleErrorModal", {
        errorType: "updateOrderError",
      });
    }
    console.error(error.message);
  }
};

const onHideModal = () => {
  hasNoSignal.value = !hasNoSignal.value;
  window.location.reload();
};
</script>
<template>
  <db-button
    :class="classList"
    :variant="variant"
    :disabled="buttonDisabled"
    @click="submit"
    >{{ setButtonString }}
  </db-button>
  <AppModal
    id="not-in-terminal"
    :visible="isNotInTerminal"
    modalType="accept"
    cancelText="ok"
    @hide-modal="() => (isNotInTerminal = !isNotInTerminal)"
  >
    <!-- Modal body -->
    <div
      v-if="isNotInTerminal"
      class="p-6 flex flex-col w-full"
      data-testid="cancel"
    >
      <MapIcon></MapIcon>
      <h2
        v-html="
          $t('not_in_terminal', { terminal: order.terminal.terminalName })
        "
      ></h2>
    </div>
  </AppModal>
  <AppModal
    id="not-in-terminal"
    :visible="hasNoSignal"
    modalType="accept"
    cancelText="gps.reload"
    @hide-modal="onHideModal"
  >
    <!-- Modal body -->
    <div
      v-if="hasNoSignal"
      class="p-6 flex flex-col w-full"
      data-testid="cancel"
    >
      <GpsIcon></GpsIcon>
      <h2 v-html="$t('main.geolocation-permission')"></h2>
    </div>
  </AppModal>
</template>
